import { DatePickerInput, TimePickerModal } from "react-native-paper-dates";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { DatePickerInputProps } from "react-native-paper-dates/lib/typescript/Date/DatePickerInput.shared";
import { getDocuments } from "../services/firestoreServices";
import { Timestamp } from "firebase/firestore";
import { ActivityIndicator, View, StyleSheet } from "react-native";
import { Button, Text, useTheme } from "react-native-paper";
import { registerTranslation } from "react-native-paper-dates";

// Register Russian locale
registerTranslation("ru", {
  save: "Сохранить",
  selectSingle: "Выберите дату",
  selectMultiple: "Выберите даты",
  selectRange: "Выберите период",
  notAccordingToDateFormat: (inputFormat) =>
    `Формат даты должен быть ${inputFormat}`,
  mustBeHigherThan: (date) => `Должно быть позже ${date}`,
  mustBeLowerThan: (date) => `Должно быть раньше ${date}`,
  mustBeBetween: (startDate, endDate) =>
    `Должно быть между ${startDate} - ${endDate}`,
  dateIsDisabled: "День не разрешен",
  previous: "Предыдущий",
  next: "Следующий",
  typeInDate: "Введите дату",
  pickDateFromCalendar: "Выберите дату из календаря",
  close: "Закрыть",
  hour: "Час",
  minute: "Минута",
});

type Props = Omit<DatePickerInputProps, "locale" | "inputMode"> & {
  collection: string;
  fieldName: string;
  fieldType?: "datePicker" | "dateTime" | "dateTimeNoYear";
};

interface FirestoreDocument {
  [key: string]: Timestamp;
}

// Time picker component using react-native-paper-dates (works on all platforms)
const TimePicker = ({
  value,
  onChange,
}: {
  value: Date;
  onChange: (date: Date) => void;
}) => {
  const [visible, setVisible] = useState(false);

  const onDismiss = useCallback(() => {
    setVisible(false);
  }, []);

  const onConfirm = useCallback(
    ({ hours, minutes }: { hours: number; minutes: number }) => {
      setVisible(false);
      const newDate = new Date(value);
      newDate.setHours(hours, minutes);
      onChange(newDate);
    },
    [value, onChange],
  );

  const showTimePickerModal = () => {
    setVisible(true);
  };

  return (
    <>
      <Button
        mode="outlined"
        onPress={showTimePickerModal}
        style={styles.timeButton}
      >
        {value.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })}
      </Button>

      <TimePickerModal
        use24HourClock
        visible={visible}
        onDismiss={onDismiss}
        onConfirm={onConfirm}
        hours={value.getHours()}
        minutes={value.getMinutes()}
      />
    </>
  );
};

export const FormDatePicker = (props: Props) => {
  const [dates, setDates] = useState<Timestamp[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchDates = async () => {
      setIsLoading(true);
      try {
        const data = await getDocuments(props.collection);
        const _dates = (data as FirestoreDocument[])
          .map((item) => item[props.fieldName])
          .filter((date): date is Timestamp => date instanceof Timestamp);
        setDates(_dates);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDates();
  }, [props.collection, props.fieldName]);

  const disabledDates = useMemo(
    () => dates.map((item) => item.toDate()),
    [dates],
  );

  const handleTimeChange = (newDate: Date) => {
    props.onChange?.(newDate);
  };

  if (isLoading) {
    return (
      <View style={{ padding: 16 }}>
        <ActivityIndicator size="small" />
      </View>
    );
  }

  return (
    <View>
      <DatePickerInput
        {...props}
        locale="ru" // Use English as fallback to avoid locale issues
        testID="datePicker"
        // validRange={{
        //   disabledDates,
        // }}
        inputMode="start"
        mode="outlined"
        startWeekOnMonday
        hasError={props.error}
      />

      {props.fieldType === "dateTime" && props.value && (
        <View style={styles.timeContainer}>
          <TimePicker value={props.value} onChange={handleTimeChange} />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  timeContainer: {
    marginTop: 8,
  },
  timeButton: {
    marginTop: 4,
  },
});
